import {
  useQuery,
  QueryKey,
  QueryObserverOptions,
  UseQueryResult,
  useMutation,
  MutationKey,
  MutateOptions,
} from "react-query";

import {
  partnerListService,
  partnerCreateService,
  partnerDetailService,
  partnerUpdateService,
  partnerSettingListService,
  partnerSettingDetailService,
  partnerSettingCreateService,
  partnerSettingUpdateService,
} from "./services";

type PartnerListResponse = Awaited<ReturnType<typeof partnerListService>>;
type PartnerDetailResponse = Awaited<ReturnType<typeof partnerDetailService>>;
type PartnerSettingListResponse = Awaited<
  ReturnType<typeof partnerSettingListService>
>;
type PartnerSettingDetailResponse = Awaited<
  ReturnType<typeof partnerSettingDetailService>
>;

export const usePartners = ({
  queryKey,
  params,
  options,
}: {
  queryKey?: QueryKey | string;
  params: Parameters<typeof partnerListService>[0];
  options?: QueryObserverOptions<unknown, unknown, PartnerListResponse>;
}) => {
  const response = useQuery({
    queryKey: queryKey ?? "partnersHook",
    queryFn: () => partnerListService(params),
    ...options,
  });

  return response as UseQueryResult<PartnerListResponse>;
};

export const usePartnerDetail = ({
  queryKey,
  params,
  options,
}: {
  queryKey?: QueryKey | string;
  params: Parameters<typeof partnerDetailService>[0];
  options?: QueryObserverOptions<unknown, unknown, PartnerDetailResponse>;
}) => {
  const response = useQuery({
    queryKey: queryKey ?? "partnerDetailHooks",
    queryFn: () => partnerDetailService(params),
    ...options,
  });

  return response as UseQueryResult<PartnerDetailResponse>;
};

export const usePartnerUpdate = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<
    PartnerDetailResponse,
    unknown,
    Parameters<typeof partnerUpdateService>[0]
  >;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "partnerUpdateHooks",
    mutationFn: partnerUpdateService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};

export const usePartnerCreate = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<
    PartnerDetailResponse,
    unknown,
    Parameters<typeof partnerCreateService>[0]
  >;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "partnerCreateHooks",
    mutationFn: partnerCreateService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};

export const usePartnerSettings = ({
  queryKey,
  params,
  options,
}: {
  queryKey?: QueryKey | string;
  params: Parameters<typeof partnerSettingListService>[0];
  options?: QueryObserverOptions<unknown, unknown, PartnerSettingListResponse>;
}) => {
  const response = useQuery({
    queryKey: queryKey ?? "partnerSettingsHook",
    queryFn: () => partnerSettingListService(params),
    ...options,
  });

  return response as UseQueryResult<PartnerSettingListResponse>;
};

export const usePartnerSettingDetail = ({
  queryKey,
  params,
  options,
}: {
  queryKey?: QueryKey | string;
  params: Parameters<typeof partnerSettingDetailService>[0];
  options?: QueryObserverOptions<
    unknown,
    unknown,
    PartnerSettingDetailResponse
  >;
}) => {
  const response = useQuery({
    queryKey: queryKey ?? "partnerSettingDetailHook",
    queryFn: () => partnerSettingDetailService(params),
    ...options,
  });

  return response as UseQueryResult<PartnerSettingDetailResponse>;
};

export const usePartnerSettingUpdate = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<
    PartnerSettingDetailResponse,
    unknown,
    Parameters<typeof partnerSettingUpdateService>[0]
  >;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "partnerSettingUpdateHooks",
    mutationFn: partnerSettingUpdateService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};

export const usePartnerSettingCreate = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<
    PartnerSettingDetailResponse,
    unknown,
    Parameters<typeof partnerSettingCreateService>[0]
  >;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "partnerSettingCreateHooks",
    mutationFn: partnerSettingCreateService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};
